import { useState, useEffect } from 'react';
import axios from 'axios';
import { ApiClient } from 'Foundation/Api';
import { useLocation } from 'react-router-dom';
import { config } from '../../../../config';

export const useNotificationApi = (authContext: any) => {
  const scopes = [config.api.hbtApiScope];
  const endpointUrl = config.notificationApi.urls.notification;
  const timeout = config.notificationApi.requestTimeout;
  const cancelSource = axios.CancelToken.source();

  const [errorCount, setErrorCount] = useState(0);
  const [authToken, setAuthToken] = useState<string | undefined>(undefined);

  let tokenRequestPromise: Promise<string> | null = null;

  const getToken = async (): Promise<string> => {
    if (tokenRequestPromise) {
      return tokenRequestPromise; // Return the existing token request if already in progress
    }
    tokenRequestPromise = (async () => {
      try {
        const token = await authContext.getToken(scopes);
        if (!token) {
          throw new Error('Token acquisition failed, received undefined or void.');
        }
        return token;
      } catch (error: any) {
        tokenRequestPromise = null; // Reset only if there's an error
        throw new Error(error);
      }
    })();
    return tokenRequestPromise.finally(() => {
      tokenRequestPromise = null; // Reset after completion (only on success)
    }) as Promise<string>;
  };

  const api = axios.create({
    timeout
  });

  const { getWithAuth, putWithAuth } = ApiClient(authContext, {
    timeout: config.userApi.requestTimeout
  });

  const getNotifications = async () => {
    try {
      const { data: responseData } = await getWithAuth(endpointUrl, {
        cancelToken: cancelSource.token
      });
      if (errorCount > 0) {
        setErrorCount(0);
      }
      return responseData.data || [];
    } catch (err) {
      setErrorCount((prev) => prev + 1);

      return [];
    }
  };

  const updateNotifications = async (notificationId?: string) => {
    let reqAuthtoken = authToken;

    if (reqAuthtoken === undefined) {
      reqAuthtoken = await getToken();
      setAuthToken(reqAuthtoken);
    }

    const notificationIdParam = notificationId != null ? `/${notificationId}` : '';
    api({
      method: 'put',
      url: `${endpointUrl}${notificationIdParam}`.trim(),
      headers: { Authorization: `Bearer ${reqAuthtoken}` }
    });
  };

  useEffect(() => {
    return () => {
      cancelSource.cancel();
    };
  }, []);

  return { errorCount, getNotifications, updateNotifications };
};
